import { Play } from "lucide-react";
import { PageBlock, PageDesign, PageSocial } from "../../../types";
import { SocialGlyph } from "./socialIcons";

export const FONT_STACK: Record<string, string> = {
  Inter: "Inter, system-ui, sans-serif",
  Poppins: "Poppins, system-ui, sans-serif",
  "Open Sans": "'Open Sans', system-ui, sans-serif",
  Roboto: "Roboto, system-ui, sans-serif",
  Montserrat: "Montserrat, system-ui, sans-serif",
  "Playfair Display": "'Playfair Display', Georgia, serif",
};

interface Props {
  title: string;
  bio?: string;
  avatarImage?: string;
  avatarColor: string;
  blocks: PageBlock[];
  socials: PageSocial[];
  design: PageDesign;
}

const PagePreview = ({ title, bio, avatarImage, avatarColor, blocks, socials, design: d }: Props) => {
  const bg =
    d.bg_type === "color"
      ? d.bg_color
      : `linear-gradient(160deg, ${d.bg_gradient_from}, ${d.bg_gradient_to})`;
  const radius = d.button_shape === "pill" ? 999 : d.button_shape === "square" ? 6 : 16;
  const shadow =
    d.button_shadow === "none"
      ? "none"
      : d.button_shadow === "hard"
      ? "4px 4px 0 rgba(24,24,27,.85)"
      : `0 10px 24px -12px ${d.button_color}cc`;

  const socialsRow = socials.filter((s) => s.url).length > 0 && (
    <div className="flex justify-center gap-3.5 mb-5" style={{ color: d.text_color, opacity: 0.8 }}>
      {socials.filter((s) => s.url).map((s, i) => (
        <span key={i}><SocialGlyph platform={s.platform} size={20} /></span>
      ))}
    </div>
  );

  return (
    <div
      className="px-6 py-8 text-center min-h-full"
      style={{ background: bg, color: d.text_color, fontFamily: FONT_STACK[d.font] || FONT_STACK.Inter }}
    >
      {avatarImage ? (
        <img src={avatarImage} alt="" className="w-20 h-20 rounded-full mx-auto mb-3 object-cover" />
      ) : (
        <div
          className="w-20 h-20 rounded-full mx-auto mb-3 flex items-center justify-center text-white font-bold text-3xl"
          style={{ background: `linear-gradient(135deg, ${avatarColor}, ${avatarColor}cc)` }}
        >
          {(title || "?").charAt(0).toUpperCase()}
        </div>
      )}
      <h1 className="font-bold text-xl" style={{ color: d.text_color }}>{title || "Your title"}</h1>
      {bio && <p className="text-sm mt-1 mb-4" style={{ opacity: 0.7 }}>{bio}</p>}
      {!bio && <div className="h-3" />}

      {d.social_placement !== "bottom" && socialsRow}

      <div className="space-y-3 text-left">
        {blocks.length === 0 && (
          <div className="text-center text-sm py-6" style={{ opacity: 0.5 }}>Add blocks to see them here</div>
        )}
        {blocks.map((b, i) => {
          if (b.type === "header")
            return b.text ? (
              <h2 key={i} className="text-center font-bold text-[15px] mt-3 mb-0.5" style={{ color: d.text_color }}>{b.text}</h2>
            ) : null;
          if (b.type === "divider")
            return <hr key={i} className="my-1 border-0 h-px" style={{ background: d.text_color, opacity: 0.14 }} />;
          if (b.type === "text")
            return b.text ? <p key={i} className="text-sm text-center" style={{ opacity: 0.85 }}>{b.text}</p> : null;
          if (b.type === "image")
            return b.image_url ? <img key={i} src={b.image_url} alt="" className="w-full" style={{ borderRadius: radius }} /> : null;
          if (b.type === "youtube")
            return (
              <div key={i} className="flex items-center justify-center gap-2 text-white text-sm font-semibold py-6" style={{ background: "#000", borderRadius: radius }}>
                <Play size={16} /> YouTube video
              </div>
            );
          if (b.type === "form")
            return (
              <div key={i} className="flex flex-col gap-2 py-1">
                {b.label && (
                  <div className="text-center font-bold text-[14px]" style={{ color: d.text_color }}>{b.label}</div>
                )}
                {b.description && (
                  <div className="text-center text-xs" style={{ opacity: 0.8 }}>{b.description}</div>
                )}
                {(b.fields || []).map((f, fi) => (
                  <div
                    key={fi}
                    className="w-full px-3 py-2 text-xs rounded-lg"
                    style={{ border: `1px solid ${d.text_color}28`, background: `${d.text_color}0a`, color: d.text_color, opacity: 0.7 }}
                  >
                    {f.label || f.key}
                  </div>
                ))}
                <div
                  className="text-center px-4 py-2.5 text-sm font-semibold"
                  style={{ background: d.button_color, color: d.button_text_color, borderRadius: radius }}
                >
                  {b.button_label || "Submit"}
                </div>
              </div>
            );
          // link
          if (!b.url) return null;
          return (
            <div
              key={i}
              className="flex items-center gap-3 px-4 py-3.5 text-sm font-semibold"
              style={{
                background: d.button_color,
                color: d.button_text_color,
                borderRadius: radius,
                boxShadow: b.featured
                  ? `0 0 0 2px ${d.button_color}, 0 12px 28px -10px ${d.button_color}cc`
                  : shadow,
              }}
            >
              {b.image_url && <img src={b.image_url} alt="" className="w-9 h-9 rounded-lg object-cover shrink-0" />}
              <span className="flex-1 min-w-0 text-center">
                <span className="block truncate">{b.label || b.url}</span>
                {b.description && <span className="block text-xs font-normal truncate" style={{ opacity: 0.85 }}>{b.description}</span>}
              </span>
            </div>
          );
        })}
      </div>

      {d.social_placement === "bottom" && <div className="mt-5">{socialsRow}</div>}
    </div>
  );
};

export default PagePreview;
